import React, { useContext } from 'react';
import Checkbox from '../Checkbox';
import EditDate from '../crm/shared/field_inputs/date/EditDate';
import EditItem from '../crm/shared/field_inputs/item/EditItem';
import EditSelect from '../crm/shared/field_inputs/select/EditSelect';
import { baseReportTypes, getOptionsObjectValues } from './shared/translations';
import { createGetSuggestionsFromApiUrl } from '../crm/shared/field_inputs/HelpMethods';
import ExlFormModalSlim from './shared/ExlFormModalSlim';
import { HandleCreateCourseReport, HandleCreateTeacherReport, HandleCreateCertificateReport } from './requests/ReportRequests';
import { filterReportFormComponents, getReportCheckboxValue, validateReportCheckboxesForCourseReport, validateReportCheckboxesForTeacherReport } from './ReportHelperFunctions';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { DataContext as CovDataStore } from '../../store';
import { CREATE_REPORT_BASE_TYPE_LABEL, CREATE_REPORT_CHOOSE_ONE_OPTION_WARN, CREATE_REPORT_COURSE_LABEL, CREATE_REPORT_FROM_LABEL, CREATE_REPORT_LABEL, CREATE_REPORT_NO_COURSE_WARN, CREATE_REPORT_NO_FROM_DATE_VALI, CREATE_REPORT_NO_TEACHER_WARN, CREATE_REPORT_NO_TO_DATE_VALI, CREATE_REPORT_NUM_CANCELLATIONS_LABEL, CREATE_REPORT_NUM_CERTIFICATIONS_LABEL, CREATE_REPORT_NUM_COURSES_LABEL, CREATE_REPORT_NUM_PARTICIPANTS_LABEL, CREATE_REPORT_NUM_WORKED_HOURS_LABEL, CREATE_REPORT_PARTICIPANT_INFO_LABEL, CREATE_REPORT_TEACHER_LABEL, CREATE_REPORT_TO_LABEL, REPORT_NUM_CANCELLATIONS_OPT, REPORT_NUM_CERT_OPT, REPORT_NUM_COURSES_OPT, REPORT_NUM_HOURS_OPT, REPORT_NUM_PARTICIPANT_OPT } from '../../i18n/keyConstants';

const CreateReport = ({ open, onClose }) => {
    const { t } = useTranslation(['createReport', 'shared']);
    const { store: covStore } = useContext(CovDataStore);
    const user = covStore?.user;

    const baseTypesOptions = getOptionsObjectValues(baseReportTypes)
        .filter(x => (user.isAdmin || user.isExlAdmin)
            || (user.isExlCertificateAdmin &&
                (x.value === baseReportTypes.AllCertificates.value ||
                    x.value === baseReportTypes.BonusCertificates.value)));
    const defaultFormValues = {
        baseType: baseTypesOptions[0]
    };

    const formComponents = [
        {
            propName: 'baseType',
            label: t(CREATE_REPORT_BASE_TYPE_LABEL),
            component: <EditSelect options={baseTypesOptions} />
        },
        {
            propName: 'teacherSuggestions',
            label: t(CREATE_REPORT_TEACHER_LABEL),
            validate: (v) => (!v ? t(CREATE_REPORT_NO_TEACHER_WARN) : ''),
            component:
                <EditItem
                    iconName='teacher'
                    isClearable
                    getSuggestions={createGetSuggestionsFromApiUrl('/api/exlTeacherFront/getTeacherSuggestions')} />
        },
        {
            propName: 'courseSuggestions',
            label: t(CREATE_REPORT_COURSE_LABEL),
            validate: (v) => (!v ? t(CREATE_REPORT_NO_COURSE_WARN) : ''),
            component:
                <EditItem
                    iconName='teacher'
                    isClearable
                    getSuggestions={createGetSuggestionsFromApiUrl('/api/exlCourseFront/getCourseSuggestions')} />
        },
        {
            propName: 'numBookings',
            validate: ((v) => validateReportCheckboxesForCourseReport() === false ? t(CREATE_REPORT_CHOOSE_ONE_OPTION_WARN) : ''),
            component: <Checkbox
                callback={getReportCheckboxValue}
                label={t(CREATE_REPORT_NUM_PARTICIPANTS_LABEL)}
                validationValue={REPORT_NUM_PARTICIPANT_OPT}
                usedInExl={true} />
        },
        {
            propName: 'participantInfo',
            validate: ((v) => validateReportCheckboxesForCourseReport() === false ? t(CREATE_REPORT_CHOOSE_ONE_OPTION_WARN) : ''),
            component: <Checkbox
                callback={getReportCheckboxValue}
                label={t(CREATE_REPORT_PARTICIPANT_INFO_LABEL)}
                validationValue={REPORT_NUM_PARTICIPANT_OPT}
                usedInExl={true} />
        },
        {
            propName: 'numCancellations',
            validate: ((v) => validateReportCheckboxesForCourseReport() === false ? t(CREATE_REPORT_CHOOSE_ONE_OPTION_WARN) : ''),
            component: <Checkbox
                callback={getReportCheckboxValue}
                label={t(CREATE_REPORT_NUM_CANCELLATIONS_LABEL)}
                labelWidth={103}
                validationValue={REPORT_NUM_CANCELLATIONS_OPT}
                usedInExl={true} />
        },
        {
            propName: 'numCourses',
            validate: ((v) => validateReportCheckboxesForTeacherReport() === false ? t(CREATE_REPORT_CHOOSE_ONE_OPTION_WARN) : ''),
            component: <Checkbox
                callback={getReportCheckboxValue}
                label={t(CREATE_REPORT_NUM_COURSES_LABEL)}
                validationValue={REPORT_NUM_COURSES_OPT}
                usedInExl={true} />
        },
        {
            propName: 'numHours',
            validate: ((v) => validateReportCheckboxesForTeacherReport() === false ? t(CREATE_REPORT_CHOOSE_ONE_OPTION_WARN) : ''),
            component: <Checkbox
                callback={getReportCheckboxValue}
                label={t(CREATE_REPORT_NUM_WORKED_HOURS_LABEL)}
                labelWidth={96}
                validationValue={REPORT_NUM_HOURS_OPT}
                usedInExl={true} />
        },
        {
            propName: 'startDate',
            validate: ((v) => !v ? t(CREATE_REPORT_NO_FROM_DATE_VALI) : ''),
            label: t(CREATE_REPORT_FROM_LABEL),
            component: <EditDate />,
        },
        {
            propName: 'endDate',
            validate: ((v) => !v ? t(CREATE_REPORT_NO_TO_DATE_VALI) : ''),
            label: t(CREATE_REPORT_TO_LABEL),
            component: <EditDate />,
        },
    ];

    const handleAdd = async (report) => {
        report.language = localStorage.getItem('userLocale');

        if (report.baseType.value === baseReportTypes.Teacher.value) {
            report.teacherId = report.teacherSuggestions.id;
            report.includeAllTeachers = false;
            report.startDate = moment(report.startDate).utc();
            report.endDate = moment(report.endDate).utc();
            return await HandleCreateTeacherReport(report);
        }
        else if (report.baseType.value === baseReportTypes.AllTeachers.value) {
            report.includeAllTeachers = true;
            report.startDate = moment(report.startDate).utc();
            report.endDate = moment(report.endDate).utc();
            return await HandleCreateTeacherReport(report);
        }
        else if (report.baseType.value === baseReportTypes.Course.value) {
            report.courseId = report.courseSuggestions.id;
            report.includeAllCourses = false;
            return await HandleCreateCourseReport(report);
        }
        else if (report.baseType.value === baseReportTypes.AllCourses.value) {
            report.includeAllCourses = true;
            report.startDate = moment(report.startDate).utc();
            report.endDate = moment(report.endDate).utc();
            return await HandleCreateCourseReport(report);
        }
        else if (report.baseType.value === baseReportTypes.AllCertificates.value) {
            report.startDate = moment(report.startDate).utc();
            report.endDate = moment(report.endDate).utc();
            report.showOnlyBonusCertifications = false;
            return await HandleCreateCertificateReport(report);
        }
        else if (report.baseType.value === baseReportTypes.BonusCertificates.value) {
            report.startDate = moment(report.startDate).utc();
            report.endDate = moment(report.endDate).utc();
            report.showOnlyBonusCertifications = true;
            return await HandleCreateCertificateReport(report);
        }
    };

    return (
        <ExlFormModalSlim
            open={open}
            onClose={onClose}
            description={t(CREATE_REPORT_LABEL)}
            defaultFormComponents={formComponents}
            handleAdd={handleAdd}
            callback={filterReportFormComponents}
            defaultFormValues={defaultFormValues}
            isExl
        />
    );
};
export default CreateReport;